import React, { useEffect, useState, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import './InlinePopover.css';

/**
 * Sales Tools — Phase 3.3.
 *
 * A small, click-outside-dismissible popover anchored to a button. The
 * distinction from a Modal is deliberate: no backdrop, the page stays
 * interactive underneath, and the popover positions itself against the
 * anchor element rather than centering on the viewport.
 *
 * Props:
 *   isOpen      — boolean, controls visibility
 *   onClose     — () => void
 *   anchorEl    — the DOM element the popover positions against
 *   placement   — 'bottom-start' | 'bottom-end' | 'top-start' | 'top-end'
 *                 (default 'bottom-end' which keeps the popover inside
 *                  narrow row-action columns)
 *   width       — pixel number, default 240
 *   children    — popover content (typically a list of action buttons)
 *
 * Behaviour:
 *   - Portal-rendered into document.body so it escapes overflow:hidden
 *     parents (Orders table cell clipping was the original trigger).
 *   - Recomputes position on scroll + resize.
 *   - Click outside the popover closes it. Escape closes it.
 *   - Clamps position to keep the popover inside the viewport.
 */
export default function InlinePopover({
  isOpen,
  onClose,
  anchorEl,
  placement = 'bottom-end',
  width = 240,
  children,
}) {
  const popoverRef = useRef(null);
  const [pos, setPos] = useState({ top: 0, left: 0, ready: false });

  const updatePosition = useCallback(() => {
    if (!anchorEl) return;
    const rect = anchorEl.getBoundingClientRect();
    const margin = 6;
    const vw = window.innerWidth;
    const vh = window.innerHeight;

    let top;
    let left;

    if (placement.startsWith('top')) {
      // Place above the anchor — top edge = anchor.top - popover height
      // (height isn't known until after first render; refine on second
      // pass using the ref's offsetHeight).
      const h = popoverRef.current ? popoverRef.current.offsetHeight : 0;
      top = rect.top - h - margin;
    } else {
      top = rect.bottom + margin;
    }

    if (placement.endsWith('end')) {
      left = rect.right - width;
    } else {
      left = rect.left;
    }

    // Clamp to viewport with 8px breathing room.
    left = Math.max(8, Math.min(left, vw - width - 8));
    top = Math.max(8, Math.min(top, vh - 8));

    setPos({ top, left, ready: true });
  }, [anchorEl, placement, width]);

  useEffect(() => {
    if (!isOpen) return undefined;
    updatePosition();
    // Second pass after popover has actually rendered (so top-placement
    // can use the real height).
    const raf = requestAnimationFrame(updatePosition);
    return () => cancelAnimationFrame(raf);
  }, [isOpen, updatePosition]);

  useEffect(() => {
    if (!isOpen) return undefined;

    const handleScroll = () => updatePosition();
    const handleResize = () => updatePosition();
    const handleEscape = (e) => {
      if (e.key === 'Escape') onClose();
    };
    const handleClick = (e) => {
      if (popoverRef.current && popoverRef.current.contains(e.target)) return;
      if (anchorEl && anchorEl.contains(e.target)) return;
      onClose();
    };

    window.addEventListener('scroll', handleScroll, true);
    window.addEventListener('resize', handleResize);
    document.addEventListener('keydown', handleEscape);
    // Click handler runs in capture phase so it fires before any
    // bubbling stopPropagation in row action cells.
    document.addEventListener('mousedown', handleClick, true);

    return () => {
      window.removeEventListener('scroll', handleScroll, true);
      window.removeEventListener('resize', handleResize);
      document.removeEventListener('keydown', handleEscape);
      document.removeEventListener('mousedown', handleClick, true);
    };
  }, [isOpen, onClose, anchorEl, updatePosition]);

  if (!isOpen || !anchorEl) return null;

  return createPortal(
    <div
      ref={popoverRef}
      className="sl-popover"
      role="dialog"
      style={{
        position: 'fixed',
        top: pos.top,
        left: pos.left,
        width,
        visibility: pos.ready ? 'visible' : 'hidden',
      }}
    >
      {children}
    </div>,
    document.body
  );
}
